import { permissionAllowed, UserDetails } from "@ews/react-data";
import { useL10n } from "@ews/react-localization-context";
import { PushCategory } from "@ews/zlt-events";
import { useRef, useState } from "react";
import { Locale } from "../../../../setup/locale";
import { useDialog } from "../../../Dialog";
import { useMyDateFormat, useMyPermissions, useMyProfileId } from "../../../Login/LoginProvider";
import { useModifyUserDetails, useUser, useUserDetails, useUserName } from "../../../ReactData/user";
import SettingsCol from "../../../Settings/SettingsCol/SettingsCol";
import SettingsCheckbox from "../../../Settings/SettingsComponents/SettingsCeckbox/SettingsCeckbox";
import SettingsLabel from "../../../Settings/SettingsComponents/SettingsLabel/SettingsLabel";
import SettingsSelect from "../../../Settings/SettingsComponents/SettingsSelect/SettingsSelect";
import SettingsSelectOption from "../../../Settings/SettingsComponents/SettingsSelectOption/SettingsSelectOption";
import SettingsTextInput from "../../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import SettingsTitle from "../../../Settings/SettingsTitle/SettingsTitle";
import { TooltipIcon } from "../../../ToolTip/useTooltip";
import { DetailsSettingsProps } from "./types";


const factorySettingTimerLogin = 28;

function range(length: number, begin: number = 1, step: number = 1)
{
    const list: number[] = [];

    for (let i = begin; i < length + begin; i += step) {
        list.push(i);
    }
    return list;
}

const logoffTimePermissionOptionsList: number[] = range(100, 1);
const logoffTimeAppOptionsList: number[] = [...range(357, 7, 7), Infinity];

enum AlarmView
{
    plan = 'TD_PLAN',
    rtm = '[SID_MNU_HK_EVENTS]'
}


const DetailsSettings: React.FC<DetailsSettingsProps> = ({ id }) =>
{

    const myId = useMyProfileId();
    const user = useUser(id);
    const data = useUserDetails(id);
    //const myDetails = useMyDetails();
    const dateFormat = useMyDateFormat();
    const dialog = useDialog();

    const createdBy = useUserName(user.createdById || "", true);
    const modifiedBy = useUserName(user.modifiedById || "", true);
    const myPermissions = useMyPermissions();
    const allowedToEdit = myId === id ? true : permissionAllowed(myPermissions.createUser || 0);

    const selectRef = useRef<HTMLIonSelectElement>(null);

    const modifyData = useModifyUserDetails();

    const modify = async (data: UserDetails) =>
    {
        if (!user.id) return;
        await modifyData(user.id!, data);
    };

    const { translate: t } = useL10n();

    function saveFlashingIcon(current: number, byte: number, checked: boolean)
    {
        const iconAnimation = checked ? current | byte : current ^ byte;
        modify({ iconAnimation } as UserDetails);
    }

    /*    function firstLetterUppercase(str: string)
       {
           const firstLetter = str[0].toUpperCase();
           const rest = str.slice(1);
           return firstLetter + rest;
       } */

    async function saveLogoffTimeApp(timerLogin: number)
    {

        const isBiggerAsFactorySettingValue = timerLogin > factorySettingTimerLogin;

        if (isBiggerAsFactorySettingValue) {
            const response = await dialog({
                title: t("PLG_USER_PROFILE_LST_FIELD_LOG_OFF_TIME_APP_LABEL"),
                message: t("PLG_USER_PROFILE_LST_FIELD_LOG_OFF_TIME_APP_WARNING", { "s": "30" }),
                confirmButtonTitle: t("[SID_YES]"),
                cancelButtonTitle: t("[SID_NO]")
            });

            if (!response) {
                timerLogin = factorySettingTimerLogin;
            }
        }
        modify({ timerLogin } as UserDetails);
    }

    const [errorTextCode2, setErrorTextCode2] = useState<string | undefined>(undefined);
    const [errorTextCode3, setErrorTextCode3] = useState<string | undefined>(undefined);

    return <>
        <SettingsCol>
            <SettingsLabel text={"COM_FACPMANAGEMENT_CUSTOMER_FIELD_CREATED_BY_USER_LABEL"} value={createdBy} />
            <SettingsLabel text={"COM_FACPMANAGEMENT_AUTOMATEDMAIL_CREATED"} value={user.created ? dateFormat(new Date(user.created!)) : ""} />
            <SettingsLabel text={"COM_FACPMANAGEMENT_CUSTOMER_FIELD_MODIFIED_BY_USER_LABEL"} value={modifiedBy} />
            <SettingsLabel text={"[REACT_MODIFIED_AT]"} value={user.modified ? dateFormat(new Date(user.modified!)) : ""} />
            <SettingsLabel text={"COM_FACPMANAGEMENT_USER_USER_FIELD_LASTVISIT_LABEL"} value={user.lastLogin ? dateFormat(new Date(user.lastLogin)) : ""} />

            <SettingsTextInput
                disabled={!allowedToEdit}
                text={"PLG_USER_PROFILE_LST_FIELD_ACL2KEY_LABEL"}
                inputType={"number"}
                value={data.codeLevel2}
                errorText={errorTextCode2}
                onChange={(codeLevel2) =>
                {
                    if (`${codeLevel2}`.length < 2) {
                        setErrorTextCode2(t("SID_DLG_TXT_INVALID_CODE_MIN"));
                    }
                    else if (`${codeLevel2}`.length > 10) {
                        setErrorTextCode2(t("SID_DLG_TXT_INVALID_CODE_MAX"));
                    } else {

                        modify({ codeLevel2 } as UserDetails);
                    }
                }} />

            <SettingsTextInput
                disabled={!allowedToEdit}
                text={"PLG_USER_PROFILE_LST_FIELD_ACL3KEY_LABEL"}
                inputType={"number"}
                value={data.codeLevel3}
                errorText={errorTextCode3}
                onChange={(codeLevel3) =>
                {

                    if (`${codeLevel3}`.length < 5) {
                        setErrorTextCode3(t("SID_DLG_TXT_INVALID_CODE_MIN"));
                    }
                    else if (`${codeLevel3}`.length > 10) {
                        setErrorTextCode3(t("SID_DLG_TXT_INVALID_CODE_MAX"));
                    } else {
                        modify({ codeLevel3 } as UserDetails);
                    }
                }} />

            {/*
            // don´t show Ticket 12537 https://redmine.lstwien/issues/12537#change-73169
            <SettingsSelect
                disabled={!allowedToEdit}
                text={"[REACT_ICON_COLOR]"}
                value={data.iconColor}
                onSelect={(iconColor) => modify({ iconColor } as UserDetails)}
            >
                <SettingsSelectOption text={"PLG_USER_PROFILE_LST_FIELD_ICONCOLOR_RED"} value={"red"} />
                <SettingsSelectOption text={"TPL_HATHOR_COLOUR_CHOICE_BLUE"} value={"blue"} />
                <SettingsSelectOption text={"[REACT_YELLOW]"} value={"yellow"} />
                <SettingsSelectOption text={"PLG_USER_PROFILE_LST_FIELD_ICONCOLOR_GREEN"} value={"green"} />
                <SettingsSelectOption text={"[REACT_BLACK]"} value={"black"} />
            </SettingsSelect>*
            */}
            <SettingsSelect
                disabled={!allowedToEdit}
                text={"PLG_USER_PROFILE_LST_FIELD_FIREALARM_GOTO_LABEL"}
                tooltip={<TooltipIcon text={t("PLG_USER_PROFILE_LST_FIELD_FIREALARM_GOTO_DESC")} />}
                value={data.alarmView}
                onSelect={(alarmView) => modify({ alarmView } as UserDetails)}>
                {Object.entries(AlarmView || {}).map(([key, val]) =>
                {
                    return <SettingsSelectOption key={key} text={val} value={key} />;
                })
                }
            </SettingsSelect>

            <SettingsSelect
                disabled={!allowedToEdit}
                text={"PLG_USER_PROFILE_LST_FIELD_LOG_OFF_TIME_APP_LABEL"}
                tooltip={<TooltipIcon text={t("PLG_USER_PROFILE_LST_FIELD_LOG_OFF_TIME_APP_DESC")} />}
                value={data.timerLogin}
                onSelect={(timerLogin) => saveLogoffTimeApp(timerLogin)}>
                {logoffTimeAppOptionsList.map((val) => <SettingsSelectOption key={val} text={`${val} ${t('COM_FACPMANAGEMENT_UNIT_DAYS')}` as Locale} value={val} />)}
            </SettingsSelect>
            <SettingsSelect
                disabled={!allowedToEdit}
                selectRef={selectRef}
                text={"PLG_USER_PROFILE_LST_FIELD_LOG_OFF_TIME_LABEL"}
                tooltip={<TooltipIcon text={t("PLG_USER_PROFILE_LST_FIELD_LOG_OFF_TIME_DESC")} />}
                value={data.timerAuthorization}
                onSelect={(timerAuthorization) => modify({ timerAuthorization: Number(timerAuthorization) } as UserDetails)}>
                {logoffTimePermissionOptionsList.map((val) => <SettingsSelectOption key={val} text={(val !== Infinity ? `${val} ${t('COM_FACPMANAGEMENT_UNIT_MINUTES')}` : t("[REACT_INFINITY]")) as any} value={val} />)}
            </SettingsSelect>
        </SettingsCol>
        <SettingsCol>

            <SettingsTitle text={"COM_FACPMANAGEMENT_USER_ACCOUNT_NOTIFICATIONS_FLASH"} endText={':'} />
            <SettingsCheckbox
                disabled={!allowedToEdit}
                text={"[SID_MNU_TAB_ALARMS]"}
                checked={((data.iconAnimation || 0) & PushCategory.FireAlarm) === PushCategory.FireAlarm}
                onChange={(checked) =>
                {
                    saveFlashingIcon(data.iconAnimation || 0, PushCategory.FireAlarm, checked);
                }} />
            <SettingsCheckbox
                disabled={!allowedToEdit}
                text={"[SID_DEV_STATE_PREALARM]"}
                checked={((data.iconAnimation || 0) & PushCategory.PreAlarm) === PushCategory.PreAlarm}
                onChange={(checked) =>
                {
                    saveFlashingIcon(data.iconAnimation || 0, PushCategory.PreAlarm, checked);
                }} />
            <SettingsCheckbox
                disabled={!allowedToEdit}
                text={"[SID_EV_FIRE_PREALARM_SH]"}
                checked={((data.iconAnimation || 0) & PushCategory.FireAlert) === PushCategory.FireAlert}
                onChange={(checked) =>
                {
                    saveFlashingIcon(data.iconAnimation || 0, PushCategory.FireAlert, checked);
                }} />
            <SettingsCheckbox
                disabled={!allowedToEdit}
                text={"[SID_EV_FIRE_TEST_ALARM_SH]"}
                checked={((data.iconAnimation || 0) & PushCategory.TestAlarm) === PushCategory.TestAlarm}
                onChange={(checked) =>
                {
                    saveFlashingIcon(data.iconAnimation || 0, PushCategory.TestAlarm, checked);
                }} />

        </SettingsCol>
        <SettingsCol></SettingsCol>
    </>;
};

export default DetailsSettings;
